/**
 * The rules behind the top of a residence page (owner, 2026-11-01, after
 * the korter.az review in docs/research/korter-az-2026-11-01.md): what the
 * status fact may say, which floor plans from the listings are shown, and
 * how the freshness line names its day. Pure functions, unit-tested in
 * tests/unit/residence-top.test.mjs. Nothing here makes a number up: a
 * fact the catalogue or the listings do not carry is left out.
 */

/** Baku is UTC+4 all year (no daylight saving since 2016). */
const BAKU_OFFSET_MS = 4 * 3600 * 1000;
const DAY_MS = 86_400_000;

/** "2026-11-01": the calendar day in Baku at this instant. */
export function bakuDay(ms: number): string {
  return new Date(ms + BAKU_OFFSET_MS).toISOString().slice(0, 10);
}

export type FreshKind = "today" | "yesterday" | "on";

/** How the freshness line names `day` (the Baku day the page read its
 *  data) when the reader's Baku day is `today`. A page stays cached for
 *  days, so "today" is only ever said by the browser, never baked in. */
export function freshKind(day: string, today: string): FreshKind {
  if (day === today) return "today";
  const prev = Date.parse(`${today}T00:00:00Z`) - DAY_MS;
  if (Number.isFinite(prev) && new Date(prev).toISOString().slice(0, 10) === day) return "yesterday";
  return "on";
}

export type BuildState =
  | { kind: "done"; year: number | null }
  | { kind: "building"; year: number | null }
  | { kind: "built"; year: number }
  | { kind: "handover"; year: number };

/**
 * The status fact from the catalogue. A year only where it can be true:
 * a completed building's year is not in the future, a building still
 * under construction is not handed over in a year already past (that is
 * a stale promise, korter's "next delivery 2023"), and a year outside
 * 1900..now+10 is a typo. Without a status, a past year reads as the year
 * built and a coming one as the handover. Null when there is nothing.
 */
export function buildState(
  status: string | null | undefined,
  year: number | null | undefined,
  nowYear: number,
): BuildState | null {
  const y = year != null && Number.isInteger(year) && year >= 1900 && year <= nowYear + 10 ? year : null;
  if (status === "completed") return { kind: "done", year: y != null && y <= nowYear ? y : null };
  if (status === "under_construction") return { kind: "building", year: y != null && y >= nowYear ? y : null };
  if (y == null) return null;
  return y <= nowYear ? { kind: "built", year: y } : { kind: "handover", year: y };
}

/** A price this far under the residence's ortalama AZN/m² is not the
 *  flat's price (a down payment, a typo); the backend applies the same
 *  share to the cheapest prices (services/residence_market.py). */
export const PLAUSIBLE_MIN_SHARE = 0.4;

/** The listing an active plan opens (a ListingCard from the API). */
export interface PlanListing {
  id: string;
  price: number | null;
  ppm: number | null;
}

/**
 * A plan of the residence's floor-plan archive (backend
 * GET /api/residences/{id}/plans, services/residence_plans): every plan
 * photo its listings ever showed (owner, 2026-11-01), the ones a listing
 * for sale shows now with that listing.
 */
export interface ArchivePlan<T extends PlanListing = PlanListing> {
  id: string;
  /** Our copy ("/api/media/plans/...") when `hosted`, else the site's photo. */
  image_url: string;
  hosted: boolean;
  rooms: number | null;
  area_m2: number | null;
  /** The active listing's price, else the last listing's. */
  price: number | null;
  currency: string | null;
  /** When a listing last showed it (ISO, UTC). */
  last_seen_at: string | null;
  /** A listing for sale shows it now. */
  active: boolean;
  /** That listing, the cheapest believable one, as a card. */
  listing: T | null;
}

export interface PlanTile<T extends PlanListing = PlanListing> {
  id: string;
  /** The listing a tap opens; null for a plan no listing shows now. */
  card: T | null;
  /** The tile's picture: our copy's path (`hosted`) or the site's photo. */
  photo: string;
  hosted: boolean;
  rooms: number | null;
  /** Whole m². */
  area: number | null;
  /** Null when the price is not believable as the flat's. */
  price: number | null;
  currency: string;
  /** When a listing last showed the plan (ISO): the archived tile's month. */
  lastSeen: string | null;
}

/**
 * The floor plans worth showing, from the residence's plan archive: one
 * tile per layout (room count and area to the m², the same flat type
 * posted by several sellers or kept from earlier listings). A layout for
 * sale now shows its listing with the lowest believable price; a layout
 * no listing shows any more, the one seen last. Ordered: for sale first,
 * then by rooms, area and price. A price far under `medianPpm` is left
 * off its tile while the plan stays.
 */
export function planTiles<T extends PlanListing>(
  plans: ArchivePlan<T>[], medianPpm: number | null | undefined,
): PlanTile<T>[] {
  const byLayout = new Map<string, PlanTile<T>>();
  for (const plan of plans) {
    if (!plan.image_url) continue;
    const card = plan.active && plan.listing ? plan.listing : null;
    const rooms = plan.rooms != null && plan.rooms > 0 ? plan.rooms : null;
    const area = plan.area_m2 != null && plan.area_m2 > 0 ? Math.round(plan.area_m2) : null;
    const ppm = card?.ppm ?? (plan.price != null && plan.area_m2 ? plan.price / plan.area_m2 : null);
    const believable = plan.price != null && plan.price > 0
      && (!medianPpm || !ppm || ppm >= PLAUSIBLE_MIN_SHARE * medianPpm);
    const tile: PlanTile<T> = {
      id: plan.id,
      card,
      photo: plan.image_url,
      hosted: plan.hosted,
      rooms,
      area,
      price: believable ? plan.price : null,
      currency: plan.currency || "AZN",
      lastSeen: plan.last_seen_at,
    };
    const key = rooms != null && area != null ? `${rooms}|${area}` : `plan|${plan.id}`;
    const prev = byLayout.get(key);
    if (!prev || better(tile, prev)) byLayout.set(key, tile);
  }
  const big = Number.MAX_SAFE_INTEGER;
  return [...byLayout.values()].sort((a, b) =>
    Number(!a.card) - Number(!b.card)
    || (a.rooms ?? 99) - (b.rooms ?? 99)
    || (a.area ?? big) - (b.area ?? big)
    || (a.price ?? big) - (b.price ?? big));
}

/** Which of two tiles of one layout stays: one for sale over one that is
 *  not; for sale, the lower believable price; not for sale, the later seen. */
function better<T extends PlanListing>(tile: PlanTile<T>, prev: PlanTile<T>): boolean {
  if (Boolean(tile.card) !== Boolean(prev.card)) return Boolean(tile.card);
  if (tile.card) return tile.price != null && (prev.price == null || tile.price < prev.price);
  return (tile.lastSeen ?? "") > (prev.lastSeen ?? "");
}

/** The plan large, for the photo viewer: our copy as stored, bina.az's and
 *  tap.az's original instead of the card size the index keeps. */
export function planFullImage(tile: Pick<PlanTile, "photo" | "hosted">): string {
  if (tile.hosted) return tile.photo;
  return tile.photo.replace(/^(https:\/\/(?:bina|tap)\.azstatic\.com\/uploads\/)(?:thumbnail|f460x345)\//, "$1full/");
}

const MONTHS: Record<string, string[]> = {
  az: ["yanvar", "fevral", "mart", "aprel", "may", "iyun", "iyul", "avqust", "sentyabr", "oktyabr", "noyabr", "dekabr"],
  ru: ["январь", "февраль", "март", "апрель", "май", "июнь", "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};

/** "oktyabr 2026" / "октябрь 2026" / "October 2026": the Baku month of an
 *  ISO time (UTC when it carries no zone); "" when it does not parse. */
export function monthYear(iso: string | null | undefined, locale: string): string {
  if (!iso) return "";
  const ms = Date.parse(/(?:Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms + BAKU_OFFSET_MS);
  return `${(MONTHS[locale] ?? MONTHS.az)[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Plural form for a count phrase: ru has three, az and en one / many. */
export function pluralKey(n: number, locale: string): "one" | "few" | "many" {
  if (locale === "ru") {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return "one";
    if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return "few";
    return "many";
  }
  return n === 1 ? "one" : "many";
}
